import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, X, Play } from 'lucide-react';
import SectionHead from './SectionHead';
import { EASE, Reveal } from './reveal';

const PHOTOS = [
  { src: '/assets/gallery/poster.webp',    label: 'HDPE Barge',  caption: 'Built for Work. Engineered to Last.' },
  { src: '/assets/gallery/topdown.webp',   label: 'Deck Layout', caption: '10\u2032 \u00d7 30\u2032 working platform' },
  { src: '/assets/gallery/crane.webp',     label: 'Hydraulic Crane', caption: 'Telescoping boom with electric winch' },
  { src: '/assets/gallery/silhouette.webp', label: 'On the Water', caption: 'Twin Suzuki 115 propulsion' },
  { src: '/assets/gallery/crane_lift.webp', label: 'Fresh Off the Press', caption: 'Brand new barge ready for service' },
  { src: '/assets/gallery/dockside_crew.webp', label: 'Top Side', caption: 'Bird\u2019s eye view of the HDPE barge' },
  { src: '/assets/gallery/open_water_twin.webp', label: 'Open Water', caption: 'Twin HDPE barges running together' },
  { src: '/assets/gallery/topdown_crane.webp', label: 'Workshop', caption: 'Custom teal crane mast \u2014 staged & ready' },
  { src: '/assets/gallery/trailer_ready.webp', label: 'Delivery Ready', caption: 'On the trailer, ready to ship anywhere in the lower 48' },
  { type: 'video', src: '/assets/gallery/new_delivery_leaving_shop.mp4', label: 'Fresh Off the Line', caption: 'Another new delivery getting ready to leave the shop' },
  { type: 'video', src: '/assets/gallery/running_aerial.mp4', label: 'Sea Trial', caption: 'Aerial run-out on calm coastal water' },
  { src: '/assets/gallery/running_water.webp', label: 'Underway', caption: 'Twin Suzuki power, smooth turquoise run' },
  { src: '/assets/gallery/beach_landing.webp', label: 'Beach Landing', caption: 'Spuds down at a Gulf-coast beach' },
  { src: '/assets/gallery/beach_close.webp', label: 'Close to Shore', caption: 'Working the shallows with the crane mast lowered' },
  { src: '/assets/gallery/cabin_interior.webp', label: 'Helm', caption: 'Inside the captain\u2019s cabin \u2014 dash, controls & crane remote' },
  { src: '/assets/gallery/piling_haul.webp', label: 'Hauling Pilings', caption: 'Loaded with pilings, headed to a Fort Myers canal job' },
  { src: '/assets/gallery/bare_hull.webp', label: 'Bare Hull', caption: 'HDPE hull with aluminum cross-bracing \u2014 ready for the build-out' },
  { src: '/assets/gallery/hammer_fleet_sunset.webp', label: 'The Absolute Squad', caption: 'Four hammers staged behind the excavator at golden hour' },
  { src: '/assets/gallery/hammer_solo_sunset.webp', label: 'Ready to Strike', caption: 'Galvanized, rigged, and dockside at sundown' },
  { src: '/assets/gallery/hammer_row_indoor.webp', label: 'Shop Line-Up', caption: 'Fresh off the fabrication line \u2014 pneumatics dialed in' },
  { src: '/assets/gallery/hammer_row_angle.webp', label: 'Ready to Ship', caption: 'Four hammers assembled, staged & waiting on the truck' },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <section id="gallery" className="relative py-16 sm:py-20 lg:py-32 bg-[#050608] border-y border-white/5">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <SectionHead
          eyebrow="A Closer Look at Our Barge"
          titleLines={['Photo Gallery']}
          body="See our barge in action — visit us anytime."
        />

        <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {PHOTOS.map((p, idx) => (
            <motion.button
              key={p.src}
              type="button"
              onClick={() => setActive(p)}
              className="group panel rounded-xl overflow-hidden aspect-[4/3] relative text-left"
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.65, ease: EASE, delay: (idx % 6) * 0.06 }}
            >
              {p.type === 'video' ? (
                <>
                  <video
                    src={p.src}
                    muted
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-black/55 border border-cyan-300/70 flex items-center justify-center backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                      <Play className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-300 ml-0.5" />
                    </span>
                  </div>
                </>
              ) : (
                <img
                  src={p.src}
                  alt={`${p.label} — ${p.caption} | HDPE work barge for sale, Fort Myers, FL`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-5 sm:p-6">
                <div className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em] mb-1.5">
                  {p.label}
                </div>
                <div className="font-display text-white text-base sm:text-lg font-semibold tracking-wide">
                  {p.caption}
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <Reveal className="mt-8 sm:mt-10 flex justify-center" delay={0.15}>
          <a
            href="https://instagram.com/absolutedocks"
            target="_blank"
            rel="noreferrer"
            className="btn-ghost px-6 py-3 rounded-md text-sm uppercase tracking-[0.18em] inline-flex items-center gap-2"
          >
            <Instagram className="w-4 h-4" />
            See more on Instagram
          </a>
        </Reveal>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setActive(null)}
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            className="absolute top-5 right-5 text-zinc-300 hover:text-cyan-300 transition-colors p-2"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          {active.type === 'video' ? (
            <video
              src={active.src}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={active.src}
              alt={active.label}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </section>
  );
}
